import { FC, useMemo } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Input,
  InputSize,
  Show
} from "shared/components";
import { PropertyGroupMultiSelect } from "./PropertyGroupMultiSelect";
import {
  PropertyGroupSplitBy,
  Segment
} from "views/property-groups-configuration/types";
import { XClose } from "untitledui-js-base";
import { MutiSelectMode } from "../type";
import { propertyGroupTitleMap } from "views/property-groups-configuration/constants";
import { plural } from "pluralize";
import { formatTitle } from "utils/formatTitle";

interface SegmentCardProps {
  title: string;
  segment: Segment;
  onSegmentChange: (segment: Segment) => void;
  segmentOptions?: any[];
  onRemove: () => void;
  currentStep: PropertyGroupSplitBy;
  segmentIndex: number;
  propertyTotalCount: number;
  allSegments?: Segment[];
  errors?: {
    name: boolean;
    options: boolean;
    duplicateOptions?: string[];
    duplicateName?: boolean;
  };
  isLastStep?: boolean;
  clearValidationError?: (fieldType: "name" | "options") => void;
}

const DisabledField = ({ label, text }: { label: string; text: string }) => {
  return (
    <div className="space-y-1.5">
      <label className="text-content-700 text-sm font-medium">{label}</label>
      <p className="text-content-300 text-sm font-medium py-[10px] px-[14px] border border-alpha-100 bg-alpha-40 rounded-xl">
        {text}
      </p>
    </div>
  );
};

export const SegmentCard: FC<SegmentCardProps> = ({
  title,
  segment,
  onSegmentChange,
  segmentOptions,
  onRemove,
  currentStep,
  segmentIndex,
  propertyTotalCount,
  allSegments = [],
  errors,
  isLastStep = false,
  clearValidationError
}) => {
  const options = segmentOptions?.map((o) => o.group_value);

  const optionsUsedInOtherSegments = useMemo(() => {
    if (allSegments.length === 0) return [];

    return allSegments
      .filter((seg, idx) => idx !== segmentIndex)
      .flatMap((seg) => seg.options);
  }, [allSegments, segmentIndex]);

  const optionsWithSegmentMap = useMemo(() => {
    if (allSegments.length === 0) return {};

    const map: Record<string, string> = {};

    allSegments.forEach((seg, idx) => {
      if (idx === segmentIndex) return;

      seg.options.forEach((option) => {
        map[option] =
          seg.name || (isLastStep ? `Group ${idx + 1}` : `Segment ${idx + 1}`);
      });
    });

    return map;
  }, [allSegments, segmentIndex, isLastStep]);

  const optionPropertyMap = useMemo(() => {
    return new Map(
      segmentOptions?.map((option) => [
        option.group_value,
        option.total_properties || 0
      ])
    );
  }, [segmentOptions]);

  const segmentPropertyCount = useMemo(() => {
    let totalFromAllSegments = 0;
    if (allSegments.length > 0) {
      const allOptions = allSegments.flatMap((seg) => seg.options);
      for (const optionValue of allOptions)
        totalFromAllSegments += optionPropertyMap.get(optionValue) || 0;
    }

    let currentSegmentCount = 0;
    if (segment.isDisabled)
      currentSegmentCount = Math.max(
        0,
        propertyTotalCount - totalFromAllSegments
      );
    else
      currentSegmentCount = segment.options.reduce(
        (total, option) => total + (optionPropertyMap.get(option) || 0),
        0
      );

    return currentSegmentCount;
  }, [
    allSegments,
    segment.options,
    segment.isDisabled,
    optionPropertyMap,
    propertyTotalCount
  ]);

  return (
    <div className="relative animate-bounce-in border border-alpha-100 rounded-lg p-6 bg-background-1000">
      <div className="absolute top-[-18px] left-1/2 transform -translate-x-1/2 bg-white px-3 py-2 border border-alpha-100 rounded-full flex items-center justify-center">
        <span className="text-content-700 font-bold text-xs flex items-center gap-1.5">
          {title}

          {segment.isDisabled ? null : (
            <button onClick={onRemove}>
              <XClose className="size-4 stroke-content-400" />
            </button>
          )}
        </span>
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          {segment.isDisabled && !segment.isOthersSegment ? (
            <DisabledField
              label={isLastStep ? "Group name" : "Segment name"}
              text={segment.name}
            />
          ) : (
            <Input
              className="!mb-0"
              size={InputSize.SM}
              id="segment-name"
              label={
                <p className="text-content-700 text-sm font-medium">
                  {isLastStep ? "Group name" : "Segment name"}
                </p>
              }
              placeholder={
                isLastStep ? "Enter group name" : "Enter segment name"
              }
              value={segment.name}
              onChange={(e) => {
                const newValue = e.target.value;
                if (!newValue.includes("_")) {
                  onSegmentChange({ ...segment, name: newValue });
                  if (clearValidationError && errors?.name)
                    clearValidationError("name");
                }
              }}
              required
              error={
                errors?.name
                  ? errors.duplicateName
                    ? isLastStep
                      ? "Group name already exists"
                      : "Segment name already exists"
                    : isLastStep
                      ? "Group name is required"
                      : "Segment name is required"
                  : undefined
              }
            />
          )}
        </div>

        <div className="space-y-1.5">
          {segment.isDisabled ? (
            <DisabledField
              label={formatTitle(plural(propertyGroupTitleMap[currentStep]))}
              text={
                segment.isOthersSegment
                  ? `Other ${plural(propertyGroupTitleMap[currentStep])}`
                  : segment.name
              }
            />
          ) : (
            <div>
              <PropertyGroupMultiSelect
                id="states"
                label={formatTitle(plural(propertyGroupTitleMap[currentStep]))}
                mode={MutiSelectMode.LIST}
                options={options || []}
                selectedOptions={segment.options}
                disabledOptions={optionsUsedInOtherSegments}
                onSelect={(options) => {
                  onSegmentChange({ ...segment, options });
                  if (
                    clearValidationError &&
                    errors?.options &&
                    options.length > 0
                  )
                    clearValidationError("options");
                }}
                currentStep={currentStep}
                error={
                  errors?.options
                    ? `Please select at least one ${propertyGroupTitleMap[currentStep]}`
                    : undefined
                }
                segmentName={segment.name || title}
                optionsWithSegmentMap={optionsWithSegmentMap}
                isLastStep={isLastStep}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 mt-2">
          <p className="text-content-300 text-xs font-medium">
            {segmentPropertyCount}/{propertyTotalCount} properties
          </p>

          <Show if={segment.options.length > 0}>
            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.SM}
              onClick={() => onSegmentChange({ ...segment, options: [] })}
            >
              Unselect all
            </Button>
          </Show>
        </div>
      </div>
    </div>
  );
};
